/**
 * GalaxyBackground.tsx — Fundo de galáxia espiral animada do Órbita.
 *
 * Porte fiel da animação `app.iniciarGalaxia` da landing (webroot/orbita/index.html):
 * 3 braços espirais, poeira (sprites radiais), núcleo pré-renderizado,
 * ~3400 estrelas com rotação diferencial e deriva radial.
 *
 * Mesmos parâmetros/visual da fonte (ARM=0.0058, EX=1.15, EY=0.85, rot += 0.0011).
 * Toda a lógica de setup + loop vive dentro do useEffect. O canvas é posicionado
 * absoluto cobrindo o container pai; o rAF é cancelado e o listener de resize é
 * removido no cleanup (evita vazamento em re-render/StrictMode).
 */

import { useEffect, useRef } from "react";

// ---------------------------------------------------------------------------
// Tipos concretos das partículas (sem any)
// ---------------------------------------------------------------------------
interface Poeira {
  r: number;
  ang: number;
  rad: number;
  sp: HTMLCanvasElement;
  alfa: number;
}

interface Star {
  r: number;
  ang: number;
  s: number;
  a: number;
  tom: number;
  brilho: boolean;
}

export function GalaxyBackground() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const wrap = cv.parentElement;
    if (!wrap) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;

    // -----------------------------------------------------------------------
    // Geometria (recalculada no resize)
    // -----------------------------------------------------------------------
    const ARM = 0.0058;
    const EX = 1.15;
    const EY = 0.85;

    let W = 0;
    let H = 0;
    let CX = 0;
    let CY = 0;
    let S = 1;

    const dimensionar = (): void => {
      W = cv.width = Math.max(600, Math.round(wrap.clientWidth));
      H = cv.height = Math.max(800, Math.round(wrap.clientHeight));
      CX = W / 2;
      // Sem <header> aqui (fonte usava offsetTop do header); usa 0.4 da altura.
      CY = H * 0.4;
      S = (CY + 60) / 660;
    };

    // -----------------------------------------------------------------------
    // Sprites de poeira (gradiente radial pré-renderizado)
    // -----------------------------------------------------------------------
    const sprite = (rgb: string): HTMLCanvasElement => {
      const c = document.createElement("canvas");
      c.width = 128;
      c.height = 128;
      const sctx = c.getContext("2d");
      if (sctx) {
        const g = sctx.createRadialGradient(64, 64, 0, 64, 64, 64);
        g.addColorStop(0, "rgba(" + rgb + ",1)");
        g.addColorStop(0.5, "rgba(" + rgb + ",0.35)");
        g.addColorStop(1, "rgba(" + rgb + ",0)");
        sctx.fillStyle = g;
        sctx.fillRect(0, 0, 128, 128);
      }
      return c;
    };

    const spAzul = sprite("90,150,195");
    const spClaro = sprite("120,180,215");
    const spQuente = sprite("224,164,59");

    // -----------------------------------------------------------------------
    // Poeira: 3 braços × 260 pontos
    // -----------------------------------------------------------------------
    const poeira: Poeira[] = [];
    for (let arm = 0; arm < 3; arm++) {
      for (let ip = 0; ip < 260; ip++) {
        const r = 50 + Math.pow(ip / 260, 0.9) * 710;
        const quente = Math.random() > 0.9;
        poeira.push({
          r,
          ang:
            arm * ((Math.PI * 2) / 3) +
            r * ARM +
            (Math.random() - 0.5) * (0.13 + r / 5000),
          rad: 14 + Math.random() * 42,
          sp: quente ? spQuente : Math.random() > 0.5 ? spAzul : spClaro,
          alfa: 0.04 + Math.random() * 0.045 * (1 - r / 950),
        });
      }
    }

    // -----------------------------------------------------------------------
    // Núcleo estático (pré-renderizado, desenhado com o tilt do disco)
    // -----------------------------------------------------------------------
    const nucleo = document.createElement("canvas");
    nucleo.width = 640;
    nucleo.height = 640;
    const nctx = nucleo.getContext("2d");
    if (nctx) {
      const halo = (rad: number, cor: string, alfa: number): void => {
        const g = nctx.createRadialGradient(320, 320, 0, 320, 320, rad);
        g.addColorStop(0, cor.replace("A", alfa.toFixed(3)));
        g.addColorStop(1, cor.replace("A", "0"));
        nctx.fillStyle = g;
        nctx.fillRect(0, 0, 640, 640);
      };
      halo(300, "rgba(240,222,190,A)", 0.16);
      halo(150, "rgba(250,235,205,A)", 0.3);
      halo(70, "rgba(255,248,230,A)", 0.5);
    }

    // -----------------------------------------------------------------------
    // Estrelas: braços (justas) + halo (dispersas) + brilhantes
    // -----------------------------------------------------------------------
    const stars: Star[] = [];
    for (let ist = 0; ist < 3400; ist++) {
      const armS = ist % 3;
      const haloS = ist % 5 === 0;
      const rS = Math.pow(Math.random(), haloS ? 0.55 : 0.75) * 760 + 14;
      const espalhe = haloS ? 2.4 : 0.2 + rS / 2200;
      const brilho = !haloS && Math.random() > 0.965;
      stars.push({
        r: rS,
        ang:
          armS * ((Math.PI * 2) / 3) +
          rS * ARM +
          (Math.random() - 0.5) * espalhe,
        s: brilho ? 2.6 + Math.random() * 1.5 : Math.random() * 1.9 + 0.6,
        a: (haloS ? 0.42 : 0.62) * Math.random() + 0.2,
        tom: Math.random(),
        brilho,
      });
    }

    // -----------------------------------------------------------------------
    // Loop de animação
    // -----------------------------------------------------------------------
    let rot = 0;
    let rafId = 0;

    const draw = (): void => {
      rot += 0.0011;
      ctx.clearRect(0, 0, W, H);
      ctx.save();
      ctx.translate(CX, CY);
      ctx.scale(EX * S, EY * S);
      ctx.drawImage(nucleo, -320, -320);
      ctx.restore();

      for (let ipd = 0; ipd < poeira.length; ipd++) {
        const p = poeira[ipd];
        if (!p) continue;
        const ap = p.ang + rot * (1 + 130 / (p.r + 60));
        const x = CX + Math.cos(ap) * p.r * EX * S;
        const y = CY + Math.sin(ap) * p.r * EY * S;
        const rd = p.rad * S;
        ctx.globalAlpha = p.alfa;
        ctx.drawImage(p.sp, x - rd, y - rd, rd * 2, rd * 2);
      }

      for (let isd = 0; isd < stars.length; isd++) {
        const st = stars[isd];
        if (!st) continue;
        st.r -= 0.055;
        if (st.r < 14) st.r = 775;
        const as = st.ang + rot * (1 + 130 / (st.r + 60));
        const xs = CX + Math.cos(as) * st.r * EX * S;
        const ys = CY + Math.sin(as) * st.r * EY * S;
        const alfa =
          st.a * Math.max(0.45, Math.min(1, (790 - st.r) / 560 + 0.3));
        const cor =
          st.tom > 0.85 ? "#F5D9A0" : st.tom > 0.5 ? "#E6F3F8" : "#9FCBDD";
        ctx.globalAlpha = alfa;
        ctx.fillStyle = cor;
        if (st.brilho) {
          ctx.drawImage(spClaro, xs - st.s * 2, ys - st.s * 2, st.s * 4, st.s * 4);
        } else {
          ctx.fillRect(xs - st.s / 2, ys - st.s / 2, st.s, st.s);
        }
      }
      ctx.globalAlpha = 1;
      rafId = requestAnimationFrame(draw);
    };

    // -----------------------------------------------------------------------
    // Resize: redimensiona canvas + recalcula geometria
    // -----------------------------------------------------------------------
    const onResize = (): void => {
      dimensionar();
    };

    dimensionar();
    draw();
    window.addEventListener("resize", onResize);

    // -----------------------------------------------------------------------
    // Cleanup: cancela rAF pendente e remove listener (StrictMode-safe)
    // -----------------------------------------------------------------------
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        background: "#0B1B2B",
        opacity: 0.85,
        pointerEvents: "none",
      }}
    />
  );
}
